import { useState } from 'react';
import { FlaskConical } from 'lucide-react';
import { stepName, type CampaignSpec } from '@vagusx/shared';
import type { WorkflowOption } from '@vagusx/clients';
import { Button } from '@/components/ui/button';
import { TestCallSheet, type TestTarget } from '@/pages/setup/workflows/TestCallSheet';
import { PreviewColumn } from './PreviewColumn';

/**
 * Rehearse (ADR-318, P11): is this what you meant? Nothing here calls a
 * patient. The calendar runs the driver's own decision function against a real
 * clock; the test call is the builder's browser test of a step's workflow,
 * where you play the patient and nothing is booked or sent.
 */
export function RehearseStep({
  spec,
  workflowOptions,
  active,
}: {
  spec: CampaignSpec;
  workflowOptions: WorkflowOption[] | undefined;
  /** The Rehearse tab is showing; the calendar simulates only then. */
  active: boolean;
}): React.ReactElement {
  const [target, setTarget] = useState<TestTarget | null>(null);

  // One test per distinct workflow: two steps running the same one would
  // rehearse the same conversation twice.
  const workflows = spec.sequence.flatMap((step, index) => {
    const option = workflowOptions?.find((o) => o.id === step.workflow?.workflowDefnId);
    return option ? [{ option, step: stepName(step, index) }] : [];
  });
  const distinct = workflows.filter(
    (w, i) => workflows.findIndex((x) => x.option.id === w.option.id) === i
  );

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <PreviewColumn
        spec={spec}
        title="One patient, call by call"
        variant="card"
        enabled={active}
      />

      <section className="h-fit space-y-3 rounded-lg border p-4">
        <h3 className="text-sm font-medium">Call yourself</h3>
        <p className="text-xs text-muted-foreground">
          Talk to the workflow in your browser, playing a test patient. Nothing is booked, texted or
          recorded as a result. It runs the workflow as it stands in the builder; the campaign runs
          the version locked in when it is published.
        </p>

        {distinct.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Choose a workflow for a step first, on the Steps screen.
          </p>
        ) : (
          <ul className="space-y-2">
            {distinct.map(({ option, step }) => (
              <li key={option.id} className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">{option.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {step}
                    {workflows.filter((w) => w.option.id === option.id).length > 1 && ' and more'}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setTarget({ workflowId: option.id, workflowName: option.name })}
                >
                  <FlaskConical className="mr-2 h-3.5 w-3.5" />
                  Test call
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <TestCallSheet target={target} autoStart onClose={() => setTarget(null)} />
    </div>
  );
}
